/**
 * 活跃路线图的 UI 数据。这里只保留尚未完成的 D1–D9；完成历史由 Git 和发行说明承载。
 * 权威说明见 docs/缺口盘点与路线图.md。
 */

type ItemStatus = 'done' | 'in_progress' | 'todo' | 'blocked' | 'decision';
export type Priority = 'P0' | 'P1' | 'P2' | 'P3';
type Owner = 'dev' | 'you' | 'partner';
export type Discipline =
  | 'product'
  | 'engineering'
  | 'growth-ops'
  | 'business'
  | 'design'
  | 'qa'
  | 'legal'
  | 'infra';

export type LaneKind = '建制' | '横切';
export const LANE_KIND: Record<Discipline, LaneKind> = {
  product: '建制',
  engineering: '建制',
  'growth-ops': '建制',
  business: '建制',
  design: '横切',
  qa: '横切',
  legal: '横切',
  infra: '横切',
};

export interface DisciplineMeta {
  label: string;
  icon: string;
  color: string;
  blurb: string;
}

export const DISCIPLINE_META: Record<Discipline, DisciplineMeta> = {
  product: { label: '产品', icon: '◎', color: '#60a5fa', blurb: '价值闭环 · 产品规格' },
  engineering: { label: '研发', icon: '⚙', color: '#34d399', blurb: '架构 · 实现 · 技术债' },
  'growth-ops': { label: '运营·增长', icon: '▲', color: '#fb7185', blurb: '获客 · 内容 · 业务运营' },
  business: { label: '商务·变现', icon: '¥', color: '#f59e0b', blurb: '订阅 · 定价 · 收款' },
  design: { label: '设计·UIUX', icon: '✦', color: '#c084fc', blurb: '设计系统 · Design QA' },
  qa: { label: '测试·QA', icon: '✓', color: '#fbbf24', blurb: '自动化 · 回归 · 烟测' },
  legal: { label: '合规·法务', icon: '§', color: '#a78bfa', blurb: '协议 · 隐私 · 支付披露' },
  infra: { label: '基建·运维', icon: '⛏', color: '#22d3ee', blurb: 'CI/CD · 密钥 · 发布' },
};

export interface RoadmapItem {
  id: string;
  title: string;
  status: ItemStatus;
  category: Discipline;
  priority?: Priority;
  owner: Owner;
  note?: string;
  shippedAt?: string;
}

export interface FeedbackEntry {
  id: string;
  from: string;
  date: string;
  raw: string;
  takeaway: string;
  linkedItemId?: string;
}

export const FEEDBACK: readonly FeedbackEntry[] = [
  {
    id: 'fb-001',
    from: '首位付费用户（抖音）',
    date: '2026-05-25',
    raw: '题目现在还是太少了，可以再增多一点。大部分思维模式都一样，这种形式可以多出一点，多训练一下用户。',
    takeaway: '题库扩容要补场景多样性，不能只复制同一种思维模式。',
    linkedItemId: 'D3',
  },
  {
    id: 'fb-002',
    from: '首位付费用户（抖音）',
    date: '2026-05-25',
    raw: '题目解析偏简短，可以再增加一些解析，比如对手范围、自己的牌排列定位、还有我为什么选错了。',
    takeaway: '解析需要覆盖对手范围、手牌定位、错因和可迁移准则。',
    linkedItemId: 'D9',
  },
  {
    id: 'fb-003',
    from: '首位付费用户（抖音）',
    date: '2026-05-25',
    raw: '单纯做题可能长期不太好。可以增加牌局复盘功能，自己上传手牌，系统帮我分析决策合不合理。',
    takeaway: '复盘是高频留存场景，但要等工程基线恢复后实施。',
    linkedItemId: 'D9',
  },
];

export const ITEMS: readonly RoadmapItem[] = [
  {
    id: 'D1',
    title: 'D1 · 先修再开发：重建工程基线并清理历史债',
    status: 'in_progress',
    category: 'engineering',
    priority: 'P0',
    owner: 'dev',
    note: '安全止血、文档归真、机器门禁、代码债清扫、依赖安全补丁和新桌面包验证。',
  },
  {
    id: 'D2',
    title: 'D2 · 订阅优先：统一月付/年付与续费生命周期',
    status: 'todo',
    category: 'business',
    priority: 'P1',
    owner: 'dev',
    note: '不提供买断或终身包；不支持自动扣款的渠道使用手动续费或账单能力。',
  },
  {
    id: 'D3',
    title: 'D3 · 题库服务端化与服务端判分',
    status: 'todo',
    category: 'engineering',
    priority: 'P0',
    owner: 'dev',
    note: '公开端最小披露，会员内容经权益检查，客户端只提交原始答案。',
  },
  {
    id: 'D4',
    title: 'D4 · SQL 全部下沉 repository',
    status: 'todo',
    category: 'engineering',
    priority: 'P0',
    owner: 'dev',
    note: '路由只做协议、校验和编排；事务、幂等 claim 与查询进入 api/src/db。',
  },
  {
    id: 'D5',
    title: 'D5 · 补齐支付、权益、认证与桌面行为刻画测试',
    status: 'todo',
    category: 'qa',
    priority: 'P0',
    owner: 'dev',
    note: '重构不得改变现有外部行为；高风险状态机覆盖乱序、重放和失败路径。',
  },
  {
    id: 'D6',
    title: 'D6 · 建立统一 pnpm check 与质量棘轮',
    status: 'todo',
    category: 'infra',
    priority: 'P0',
    owner: 'dev',
    note: '类型、类型感知 lint、测试、架构、死代码、重复率、文档和凭据扫描统一阻断。',
  },
  {
    id: 'D7',
    title: 'D7 · 订阅协议、退款和支付披露归一',
    status: 'todo',
    category: 'legal',
    priority: 'P1',
    owner: 'you',
    note: '工程负责保持产品与条款一致；最终法律和服务商配置由 Yono 复核。',
  },
  {
    id: 'D8',
    title: 'D8 · 版本与部署单一真相源',
    status: 'todo',
    category: 'infra',
    priority: 'P0',
    owner: 'dev',
    note: '桌面壳版本、renderer build、tag 和产物一致；生产部署等待远端检查，高风险改动手动 canary。',
  },
  {
    id: 'D9',
    title: 'D9 · 复盘教学化 P2',
    status: 'blocked',
    category: 'product',
    priority: 'P2',
    owner: 'dev',
    note: '决策已批准；在 D1–D8 和一次新桌面安装包验证完成前暂停开发。',
  },
];
